<template>
  <b-container v-if="item">
    <b-row align-h="start" class="mb-4">
      <b-overlay :show="loading" no-wrap fixed />
      <b-col>
        <h1>Item: {{ item.$.name }}</h1>
      </b-col>
      <b-col class="text-right">
        <saved-notice v-if="saved" />
        <b-button variant="primary" class="ml-2 mb-1" @click="onSave">
          Save
        </b-button>
        <b-button
          variant="primary"
          class="ml-1 ml-sm-2 mb-1"
          @click="$router.push('/items')"
          >Back</b-button
        >
      </b-col>
    </b-row>
    <error-alert :errors="errors" class="mb-3" />
    <b-row class="base-form mb-3">
      <b-col xl="6" sm="12">
        <b-form-group label="Name">
          <b-form-input v-model="item.name" :disabled="readOnly" />
        </b-form-group>
        <b-form-group>
          <template slot="label" class="d-flex">
            Status
            <span id="single-item-status" class="icon ml-2">
              <img
                id="hours-tooltip"
                :src="asset('/imgs/icons/info.svg')"
                alt="hours info"
                class="mb-1"
              />
            </span>
          </template>

          <b-form-select
            v-model="item.status"
            :options="statusOptions"
            :disabled="readOnly"
          />
        </b-form-group>
        <b-tooltip target="single-item-status" triggers="hover">
          Change the master item status here to update the status across all
          menus. If you'd like to 86 this item on specific menus, click the 86
          checkbox in the menu items table below. The item will be unavailable
          until you re-activate it.
        </b-tooltip>
        <b-row>
          <b-col cols="6">
            <b-form-group label="Price">
              <currency-input
                v-model="item.price"
                :disabled="readOnly"
                :value-as-integer="true"
                :value-range="{
                  min: 0,
                }"
                class="form-control"
              />
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group class="mt-4">
              <b-form-checkbox
                v-model="item.taxable"
                :disabled="readOnly"
                :value="1"
                :unchecked-value="0"
                class="mb-2 mt-2"
                ><span class="ml-2">Taxable?</span></b-form-checkbox
              >
            </b-form-group>
          </b-col>
        </b-row>
        <template v-if="item.photo">
          <div>
            <span class="mr-2 col-form-label">Image</span>
            <b-link v-if="!readOnly" class="mr-2" @click="onRemoveImage()"
              >Remove</b-link
            >
          </div>
          <div>
            <img
              class="mt-3 mb-2"
              :src="awsImageUrl(item.photo, 100, 100)"
              alt="item photo"
            />
          </div>
        </template>
        <template v-else>
          <b-form-group label="Image">
            <error-alert :errors="photoError" class="mb-2" />
            <b-form-file
              v-model="photoFile"
              :disabled="readOnly"
              accept="image/jpeg, image/png, image/jpg"
              :state="Boolean(photoFile)"
              placeholder="Choose an image file or drop it here...."
              drop-placeholder="Drop file here..."
            />
            <p class="helper-text">
              Must be under 2MB and .png, .jpg, or .jpeg. Recommendend image
              ratio is 940px wide by 640px tall.
            </p>
            <b-button
              variant="primary"
              :disabled="readOnly"
              @click="onUploadImage"
              >Upload</b-button
            >
          </b-form-group>
        </template>
      </b-col>
      <b-col lg="6" sm="12">
        <b-form-group label="Key">
          <b-form-input v-model="item.import_key" :disabled="readOnly" />
        </b-form-group>
        <b-form-group label="Short Description">
          <b-form-input v-model="item.short_description" :disabled="readOnly" />
        </b-form-group>
        <b-form-group label="Long Description">
          <b-form-textarea
            v-model="item.description"
            :disabled="readOnly"
            :rows="3"
          />
        </b-form-group>
        <custom-search-field
          v-model="brand"
          :disabled="readOnly"
          group-label="Brand"
          :placeholder="'Select brand'"
          :path-to-search="'brands'"
        />
      </b-col>
    </b-row>
    <b-row v-if="menuItems" class="mb-3 overflow-auto" no-gutters>
      <table class="editable-table">
        <thead>
          <tr>
            <th class="info-holder">
              <img
                id="single-item-table-header-menus"
                :src="asset('/imgs/icons/info.svg')"
                alt="hours info"
              />
            </th>
            <th class="pl-0">Menus</th>
            <b-tooltip target="single-item-table-header-menus" triggers="hover">
              Choose a menu from the dropdown to create a menu item. If no menu
              items are listed below, this item will not display on any menus
              for customers to order.
            </b-tooltip>
            <th class="pl-3">Categories</th>
            <th class="pl-3">Kitchen</th>
            <th class="pl-3">Price</th>
            <th>86?</th>
            <th width="4%" />
          </tr>
        </thead>
        <tbody>
          <tr v-for="(menuItem, index) in menuItems" :key="menuItem.id">
            <td />
            <td>{{ menuItem.menu.name }}</td>
            <td class="pl-1">
              <custom-search-field
                v-model="menuItem.menu_categories"
                :disabled="!canEditMenuItem(menuItem)"
                class="single-input mb-0"
                :placeholder="'Type to add categories...'"
                :path-to-search="'menu-categories'"
                :multiselect="true"
                :query-params="{
                  menu: menuItem.menu.id,
                }"
              />
            </td>
            <td class="pl-1">
              <custom-search-field
                v-model="menuItem.fulfillment_center"
                :disabled="!canEditMenuItem(menuItem)"
                class="single-input mb-0"
                :placeholder="'Type to add kitchen...'"
                :path-to-search="'fulfillment-centers'"
                :query-params="{
                  facility: menuItem.menu.facility_id,
                }"
              />
            </td>
            <td class="pl-1">
              <currency-input
                v-model="menuItem.activePrice"
                :disabled="!canEditMenuItem(menuItem)"
                :value-as-integer="true"
                :value-range="{
                  min: 0,
                }"
                class="form-control single-input"
              />
            </td>
            <td>
              <b-form-checkbox
                v-model="menuItem.status"
                :disabled="!canEditMenuItem(menuItem)"
                :value="'inactive'"
                :unchecked-value="'active'"
              />
            </td>
            <td class="text-center">
              <b-button
                v-if="canEditMenuItem(menuItem)"
                variant="white"
                class="text-primary"
                @click="removeMenuItem(index)"
              >
                <b-icon icon="X" />
              </b-button>
            </td>
          </tr>
        </tbody>
        <tfoot v-if="!readOnly">
          <tr>
            <td />
            <td colspan="1">
              <custom-search-field
                v-model="newMenu"
                class="single-input table-input my-1"
                :placeholder="'Type to add menu...'"
                :path-to-search="'menus'"
              />
            </td>
          </tr>
        </tfoot>
      </table>
    </b-row>
    <b-row
      v-if="!item.itemAttributes || !readOnly"
      class="mb-3 overflow-auto"
      no-gutters
    >
      <table class="editable-table">
        <thead>
          <tr>
            <th style="width: 60px" class="text-center" />
            <th class="pl-0">Modifier</th>
            <th class="pl-0">Modifier Key</th>
            <th>Modifier Values</th>
            <th width="4%" />
          </tr>
        </thead>
        <draggable :disabled="readOnly" :list="item.itemAttributes" tag="tbody">
          <tr
            v-for="(modifier, index) in item.itemAttributes"
            :key="modifier.id"
          >
            <td v-if="!readOnly" class="text-center">
              <b-icon icon="list"></b-icon>
            </td>
            <td>{{ modifier.name }}</td>
            <td>{{ modifier.import_key }}</td>
            <td>
              <ul v-if="modifier.attribute_items.length" class="my-1 pl-4">
                <li
                  v-for="modifierItem in modifier.attribute_items"
                  :key="modifierItem.id"
                >
                  {{ modifierItem.name }} +{{
                    (modifierItem.price / 100) | currency
                  }}
                </li>
              </ul>
            </td>
            <td class="text-center">
              <b-button
                v-if="!readOnly"
                variant="white"
                class="text-primary"
                @click="removeModifier(index)"
              >
                <b-icon icon="X" />
              </b-button>
            </td>
          </tr>
        </draggable>
        <tfoot v-if="!readOnly">
          <tr>
            <td />
            <td>
              <custom-search-field
                v-model="newModifier"
                :disabled="readOnly"
                :label-field="'`${item.name} - ${item.import_key}`'"
                class="single-input table-input my-1"
                :placeholder="'Type to add modifiers...'"
                :path-to-search="'attributes'"
                :query-params="{
                  brand: item.brand_id,
                  active: 1,
                }"
              />
            </td>
          </tr>
        </tfoot>
      </table>
    </b-row>
    <!-- <b-row class="mb-3 overflow-auto" no-gutters>
      <table class="editable-table">
        <thead>
          <tr>
            <th class="info-holder">
              <img
                id="single-item-table-header-allergens"
                :src="asset('/imgs/icons/info.svg')"
                alt="hours info"
              />
            </th>
            <th width="10%" class="pl-0">Allergens</th>
            <b-tooltip
              target="single-item-table-header-allergens"
              triggers="hover"
            >
              For allergens, select 'Yes' to show that this item contains an
              allergen. Select 'No' to display an allergen-free icon on the
              item. Select 'Unknown' to do neither. For dietary preferences,
              check the box to display an icon on the item.
            </b-tooltip>
            <th width="15%" />
            <th width="20%" />
            <th class="pl-1">Dietary Preferences</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td />
            <td>Contains Dairy</td>
            <td>
              <b-form-select
                v-model="dietarySelections['Dairy']"
                :disabled="readOnly"
                :options="allergenOptions"
              />
            </td>
            <td />
            <td>
              <b-form-checkbox
                v-model="dietarySelections['Gluten-free']"
                :disabled="readOnly"
                :value="true"
                :unchecked-value="false"
              >
                <span class="ml-2">Gluten-free</span>
              </b-form-checkbox>
            </td>
          </tr>
          <tr>
            <td />
            <td>Contains Nuts</td>
            <td>
              <b-form-select
                v-model="dietarySelections['Nut']"
                :disabled="readOnly"
                :options="allergenOptions"
              />
            </td>
            <td />
            <td>
              <b-form-checkbox
                v-model="dietarySelections['Vegan']"
                :disabled="readOnly"
                :value="true"
                :unchecked-value="false"
              >
                <span class="ml-2">Vegan</span>
              </b-form-checkbox>
            </td>
          </tr>
          <tr>
            <td />
            <td />
            <td />
            <td />
            <td height="40px">
              <b-form-checkbox
                v-model="dietarySelections['Vegetarian']"
                :disabled="readOnly"
                :value="true"
                :unchecked-value="false"
              >
                <span class="ml-2">Vegetarian</span>
              </b-form-checkbox>
            </td>
          </tr>
        </tbody>
      </table>
    </b-row> -->
    <b-row class="mb-3 overflow-auto" no-gutters>
      <b-col md="12" lg="6" class="pr-2">
        <table>
          <thead>
            <tr>
              <th class="px-0 info-holder">
                <img
                  id="single-item-table-header-allergens"
                  :src="asset('/imgs/icons/info.svg')"
                  alt="hours info"
                />
              </th>
              <th class="pl-0">Allergens</th>
              <th />
              <b-tooltip
                target="single-item-table-header-allergens"
                triggers="hover"
              >
                For allergens, select 'Yes' to show that this item contains an
                allergen. Select 'No' to display an allergen-free icon on the
                item. Select 'Unknown' to do neither. For dietary preferences,
                check the box to display an icon on the item.
              </b-tooltip>
            </tr>
          </thead>
          <tbody>
            <tr v-for="allergen in allergenChoices" :key="allergen">
              <td />
              <td class="pl-0">Contains {{ allergen }}</td>
              <td>
                <b-form-select
                  v-model="allergensSelected[allergen]"
                  :options="allergenOptions"
                ></b-form-select>
              </td>
            </tr>
          </tbody>
        </table>
      </b-col>
      <b-col md="12" lg="6" class="pr-2">
        <table>
          <thead>
            <tr>
              <th class="px-0 info-holder" />
              <th class="pl-0">Dietary Restrictions</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="dietaryRestriction in dietaryRestrictions"
              :key="dietaryRestriction.id"
            >
              <td />
              <td>
                <b-form-checkbox
                  v-model="selectedDietaryRestrictions"
                  :value="dietaryRestriction.id"
                >
                  {{ dietaryRestriction.name }}
                </b-form-checkbox>
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import draggable from 'vuedraggable';
import ErrorAlert from '../../components/ErrorAlert';
import Item from '../../models/Item.js';
import ResourceAPI from '../../services/ResourceAPI';
import SavedNotice from '../../components/SavedNotice';

export default {
  name: 'Item',
  components: {
    draggable,
    ErrorAlert,
    SavedNotice,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      saved: false,
      errors: {},
      item: null,
      brand: null,
      photoError: {},
      photoFile: null,
      newModifier: null,
      originalPrice: 0,
      menuItems: [],
      menuItemsLoading: false,
      archivedMenuItems: [],
      newMenu: null,
      // dietaryRestrictions: [],
      // dietarySelections: {
      //   Dairy: null,
      //   'Dairy-free': null,
      //   Nut: null,
      //   'Nut-free': null,
      //   'Gluten-free': null,
      //   Vegan: null,
      //   Vegetarian: null,
      // },
      // selectRestrictions: ['Dairy', 'Nut'],
      // booleanRestrictions: ['Gluten-free', 'Vegan', 'Vegetarian'],
      selectedDietaryRestrictions: [],
      dietaryRestrictions: [],
      allergens: [],
      allergensSelected: {},
      statusOptions: [
        {
          value: 'active',
          text: 'Active',
        },
        {
          value: 'inactive',
          text: '86ed',
        },
        {
          value: 'archived',
          text: 'Archived',
        },
      ],
      allergenOptions: [
        {
          text: 'Unknown',
          value: null,
        },
        {
          text: 'Yes',
          value: true,
        },
        {
          text: 'No',
          value: false,
        },
      ],
    };
  },
  computed: {
    ...mapGetters('session', ['brandIds', 'kitchenIds', 'hubIds']),
    readOnly() {
      if (this.item && !this.brandIds.includes(this.item.brand_id)) {
        return true;
      }

      return false;
    },
    allergenChoices(state) {
      return state.allergens
        .filter((a) => !a.name.includes('-free'))
        .map((a) => a.name);
    },
  },
  watch: {
    brand(val) {
      this.item.set('brand_id', val ? val.id : null);
    },
    newModifier(val) {
      if (val) {
        const modifierExists = this.item.itemAttributes.filter(
          (el) => el.id === val.id
        );

        if (!modifierExists.length) {
          this.item.itemAttributes.push(val);
        }

        this.newModifier = null;
      }
    },
    newMenu(val) {
      if (val) {
        // Skip adding a duplicate menu / item combo
        if (!this.menuItems.filter((el) => el.menu_id === val.id).length) {
          this.menuItems.push({
            menu: val,
            menu_id: val.id,
            menu_categories: [],
            fulfillment_center: null,
            activePrice: this.item.price,
            status: 'active',
          });
        }

        this.newMenu = null;
      }
    },
  },
  mounted() {
    this.loadDietaryRestrictions();
    this.refreshRecord();
  },
  methods: {
    loadDietaryRestrictions() {
      const me = this;

      // Retrieve all dietary restrictions
      ResourceAPI.getRecords('dietary-restrictions')
        .then((data) => {
          let i;

          for (i = 0; i < data.data.length; i += 1) {
            if (data.data[i].type === 'dietary-restriction') {
              me.dietaryRestrictions.push({
                id: data.data[i].id,
                name: data.data[i].name,
              });
            } else if (
              data.data[i].type === 'allergen' ||
              data.data[i].type === 'negative-allergen'
            ) {
              me.allergens.push({
                id: data.data[i].id,
                name: data.data[i].name,
              });
              if (data.data[i].type === 'allergen') {
                me.allergensSelected[data.data[i].name] = null;
              }
            }
          }
        })
        .catch((error) => alert(error));
    },

    refreshRecord() {
      const me = this;

      me.loading = true;
      const itemRaw = new Item({
        id: me.id,
      });
      itemRaw
        .fetch()
        .then(() => {
          let i;
          me.item = itemRaw;
          me.brand = me.item.brand;
          me.originalPrice = me.item.price;
          me.loading = false;

          for (i = 0; i < me.item.dietary_restrictions.length; i += 1) {
            const dietaryRestriction = me.item.dietary_restrictions[i];
            if (dietaryRestriction.type === 'dietary-restriction') {
              me.selectedDietaryRestrictions.push(dietaryRestriction.id);
            } else if (dietaryRestriction.name.includes('-free')) {
              me.allergensSelected[
                dietaryRestriction.name.replace('-free', '')
              ] = false;
            } else {
              me.allergensSelected[dietaryRestriction.name] = true;
            }
          }

          // me.updateDietarySelections();
          me.loadMenuItems();
        })
        .catch((error) => {
          alert(error);
        });
    },

    loadMenuItems() {
      const me = this;

      me.menuItemsLoading = true;

      return ResourceAPI.getRecords('menu-items', {
        with: 'menu,fulfillmentCenter,menuCategories',
        item: me.id,
        limit: 100,
        status: 'active,inactive',
        fulfillmentCenter: me.readOnly ? me.kitchenIds.join(',') : null,
        menuStatus: 'active,inactive',
      })
        .then((data) => {
          me.menuItems = data.data;
          me.menuItemsLoading = false;
        })
        .catch((error) => alert(error));
    },

    canEditMenuItem(menuItem) {
      return (
        !menuItem.fulfillment_center_id ||
        this.kitchenIds.includes(menuItem.fulfillment_center_id)
      );
    },

    // Update local records with remote settings
    updateDietarySelections() {
      for (let i = 0; i < this.selectRestrictions.length; i += 1) {
        if (
          this.item.dietary_restrictions.filter(
            (el) => el.name === this.selectRestrictions[i]
          ).length
        ) {
          this.dietarySelections[this.selectRestrictions[i]] = true;
        } else if (
          this.item.dietary_restrictions.filter(
            (el) => el.name === `${this.selectRestrictions[i]}-free`
          ).length
        ) {
          this.dietarySelections[this.selectRestrictions[i]] = false;
        } else {
          this.dietarySelections[this.selectRestrictions[i]] = null;
        }
      }

      // Checkbox selections
      for (let i = 0; i < this.booleanRestrictions.length; i += 1) {
        this.dietarySelections[this.booleanRestrictions[i]] =
          this.item.dietary_restrictions.filter(
            (el) => el.name === this.booleanRestrictions[i]
          ).length > 0;
      }
    },

    // Build list of dietary restriction ids to pass to the server
    buildDietaryIds() {
      const dietaryIds = [];

      for (let i = 0; i < this.selectRestrictions.length; i += 1) {
        if (this.dietarySelections[this.selectRestrictions[i]] === true) {
          dietaryIds.push(
            this.dietaryRestrictions.find(
              (el) => el.name === this.selectRestrictions[i]
            ).id
          );
        } else if (
          this.dietarySelections[this.selectRestrictions[i]] === false
        ) {
          dietaryIds.push(
            this.dietaryRestrictions.find(
              (el) => el.name === `${this.selectRestrictions[i]}-free`
            ).id
          );
        }
      }

      // Checkbox selections
      for (let i = 0; i < this.booleanRestrictions.length; i += 1) {
        if (this.dietarySelections[this.booleanRestrictions[i]]) {
          dietaryIds.push(
            this.dietaryRestrictions.find(
              (el) => el.name === this.booleanRestrictions[i]
            ).id
          );
        }
      }

      return dietaryIds;
    },

    // Build menu item data array
    buildMenuItems() {
      const data = [];

      for (let i = 0; i < this.menuItems.length; i += 1) {
        const menuItem = this.menuItems[i];

        data.push({
          menu_id: menuItem.menu_id,
          price:
            menuItem.activePrice === this.originalPrice
              ? null
              : menuItem.activePrice,
          menu_categories: menuItem.menu_categories.map((c) => c.id),
          fulfillment_center_id: menuItem.fulfillment_center
            ? menuItem.fulfillment_center.id
            : null,
          status: menuItem.status,
        });
      }

      return data;
    },

    // Save record
    onSave() {
      if (this.readOnly) {
        this.onReadOnlySave();
        return;
      }

      const me = this;
      const modifiers = me.item.get('itemAttributes');
      const modifiersIds = [];

      me.loading = true;

      // Set selected modifier item ids
      for (let i = 0; i < modifiers.length; i += 1) {
        modifiersIds.push(modifiers[i].id);
      }
      me.item.set('attributeIds', modifiersIds);
      // Set selected dietary restriction ids
      const selectedAllergenIds = me.getSelectedAllergenIds();
      me.item.set(
        'dietaryRestrictionIds',
        me.selectedDietaryRestrictions.concat(selectedAllergenIds)
      );
      // me.itwem.set('dietaryRestrictionIds', me.buildDietaryIds());
      me.item.set('menuItems', me.buildMenuItems());

      me.item
        .save()
        .then(() => {
          me.loading = false;
          me.saved = true;
          setTimeout(() => {
            me.saved = false;
          }, 5000);
          me.errors = {};
          me.refreshRecord();
        })
        .catch((error) => {
          me.loading = false;
          me.errors = error.response.data.errors;
        });
    },

    // Update only an items menu item availability
    onReadOnlySave() {
      const me = this;

      ResourceAPI.makePostRequest('/menu-items/update', {
        data: me.buildReadOnlyMenuItemData(),
      })
        .then(() => {
          me.loading = false;
          me.saved = true;
          setTimeout(() => {
            me.saved = false;
          }, 5000);
          me.errors = {};
          me.refreshRecord();
        })
        .catch((error) => {
          me.loading = false;
          me.errors = error.response.data.errors;
        });
    },

    // Build object of menu item ids / statuses
    buildReadOnlyMenuItemData() {
      const data = {};

      for (let i = 0; i < this.menuItems.length; i += 1) {
        const menuItem = this.menuItems[i];

        if (this.canEditMenuItem(menuItem)) {
          data[menuItem.id] = {
            price:
              menuItem.activePrice === this.originalPrice
                ? null
                : menuItem.activePrice,
            menu_categories: menuItem.menu_categories.map((c) => c.id),
            fulfillment_center_id: menuItem.fulfillment_center
              ? menuItem.fulfillment_center.id
              : null,
            status: menuItem.status,
          };
        } else {
          data[menuItem.id] = {
            status: menuItem.status,
          };
        }
      }

      for (let i = 0; i < this.archivedMenuItems.length; i += 1) {
        data[this.archivedMenuItems[i]] = {
          status: 'archived',
        };
      }

      return data;
    },

    // Handle uploading image file
    onUploadImage() {
      const me = this;

      if (!me.photoFile) {
        me.photoError = {
          file: 'File missing, please select a file to upload.',
        };
        return;
      }

      me.loading = true;

      ResourceAPI.uploadFile('upload-image', {
        file: me.photoFile,
        title: me.photoFile.name,
      })
        .then((response) => {
          if (response.success) {
            me.photoError = {};
            me.photoFile = null;
            me.item.set('photo', response.data);
            me.item.set('photo_id', response.data.id);
          } else {
            me.photoError = {
              file:
                response.status === 403
                  ? "Sorry, you don't have permission to upload files."
                  : "There was an issue uploading your image, please it's verify under 2mb and a jpg, jpeg or .png.",
            };
          }
          me.loading = false;
        })
        .catch((error) => alert(error));
    },

    // Remove image from UI
    onRemoveImage() {
      this.item.set('photo', null);
      this.item.set('photo_id', null);
    },

    // Remove modifier from list
    removeModifier(index) {
      const modifiers = this.item.get('itemAttributes');

      modifiers.splice(index, 1);
      this.item.set('itemAttributes', modifiers);
    },

    // Remove menu item from list
    removeMenuItem(index) {
      if (!this.archivedMenuItems.includes(this.menuItems[index].id)) {
        this.archivedMenuItems.push(this.menuItems[index].id);
      }

      this.menuItems.splice(index, 1);
    },

    getSelectedAllergenIds() {
      const me = this;
      const ids = [];
      Object.keys(me.allergensSelected).forEach((allergen) => {
        if (me.allergensSelected[allergen] === true) {
          ids.push(me.allergens.find((a) => a.name === allergen).id);
        } else if (me.allergensSelected[allergen] === false) {
          ids.push(me.allergens.find((a) => a.name === `${allergen}-free`).id);
        }
      });
      return ids;
    },

    getAllergenLabel(allergen) {
      if (allergen.toLowerCase() === 'nut') {
        return 'Contains Nuts';
      }

      return `Contains ${allergen}`;
    },
  },
};
</script>
